import { defineStore } from 'pinia'

interface User {
  uid: string
  email: string
  displayName: string
}

// Import types from global.d.ts
type UserRole = 'user' | 'dealer-admin' | 'rep' | 'admin'

interface UserMetadata {
  uid: string
  email: string
  role: UserRole
  dealerId?: string | null
  displayName?: string | null
  createdAt?: string
  updatedAt?: string
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as User | null,
    hydrated: false,
    isAdmin: false, // Admin-flagga från Firebase custom claims
    role: null as UserRole | null, // User role from Firestore
    dealerId: null as string | null, // Assigned dealer ID
    metadata: null as UserMetadata | null, // Full user metadata
    isRepAdmin: false // Whether user is assigned to any rep's adminUserIds
  }),
  getters: {
    isAuthenticated: (s) => !!s.user,
    isAdminGetter: (s) => s.isAdmin,
    isRep: (s) => s.role === 'rep',
    isDealerAdmin: (s) => s.role === 'dealer-admin',
    isRegularUser: (s) => s.role === 'user',
    hasRole: (s) => !!s.role,
    isRepAdminGetter: (s) => s.isRepAdmin
  },
  actions: {
    setUser(u: User) {
      this.user = u
      this.hydrated = true
      this.updateAdminClaim()
      this.hydrateUserMetadata()
    },
    clearUser() {
      this.user = null
      this.isAdmin = false
      this.role = null
      this.dealerId = null
      this.metadata = null
      this.isRepAdmin = false
      this.hydrated = true
    },
    async updateAdminClaim() {
      if (typeof window === 'undefined') return
      try {
        // Dynamisk import för att undvika SSR-problem
        const { getAuth } = await import('firebase/auth')
        const auth = getAuth()
        const fbUser = auth.currentUser
        if (!fbUser) {
          this.isAdmin = false
          return
        }
        // Hämta token result med custom claims, tvinga refresh!
        const tokenResult = await fbUser.getIdTokenResult(true)
        this.isAdmin = !!tokenResult.claims?.admin
      } catch (e) {
        this.isAdmin = false
      }
    },
    async hydrateUserMetadata() {
      if (typeof window === 'undefined') return
      if (!this.user) return

      try {
        // Fetch user metadata from Firestore
        const { getFirestore, doc, getDoc, collection, query, where, getDocs } = await import('firebase/firestore')
        const { getApp } = await import('firebase/app')
        
        const app = getApp()
        const db = getFirestore(app, 'kudo-sales')
        const userDoc = await getDoc(doc(db, 'users', this.user.uid))
        
        if (userDoc.exists()) {
          const data = userDoc.data() as UserMetadata
          this.metadata = {
            uid: this.user.uid,
            email: this.user.email,
            ...data
          }
          this.role = data.role || null
          this.dealerId = data.dealerId || null
        } else {
          // User metadata doesn't exist yet - set defaults
          this.role = 'user'
          this.dealerId = null
          this.metadata = {
            uid: this.user.uid,
            email: this.user.email,
            role: 'user'
          }
        }
        
        // Check if user is a rep admin (assigned to any rep's adminUserIds)
        try {
          const repsQuery = query(collection(db, 'reps'), where('adminUserIds', 'array-contains', this.user.uid))
          const repsSnapshot = await getDocs(repsQuery)
          this.isRepAdmin = !repsSnapshot.empty
          console.log('[auth] Rep admin status:', this.isRepAdmin, 'Reps found:', repsSnapshot.size)
        } catch (repsError) {
          console.warn('[auth] Failed to check rep admin status (may be a permissions issue):', repsError)
          // Don't throw - just set to false and continue
          this.isRepAdmin = false
        }
        
      } catch (e) {
        console.error('[auth] Failed to hydrate user metadata:', e)
        this.role = null
        this.dealerId = null
        this.metadata = null
        this.isRepAdmin = false
      }
    }
  }
})